/**
 * Mock 的 SSE 流。
 *
 * 这里刻意**复刻服务端的封帧细节**，因为前端的流式解析器最难调的就是这些边界：
 * 1. 分片**不按字符边界切**。CJK 是 3 字节，`MOCK_CHUNK_SIZE = 7` 与之互质，
 *    保证把一个汉字劈成两半。调用方必须用 `TextDecoder({stream:true})` 跨片拼接，
 *    否则会在回答里出现 U+FFFD（"�"）。这一步是 UTF-8 解码器的职责，不是解析器的。
 * 2. 每 15 秒一个心跳帧（`: keep-alive\n\n`），与后端一致。它**不是**事件帧，
 *    解析器必须无声忽略——如果把注释行当事件，界面上会多出五条空消息。
 * 3. **不发 `[DONE]` 哨兵**。真实后端就是靠流关闭表示结束的；
 *    多一个哨兵会让前端出现一条"只在 Mock 下才有"的收尾路径。
 * 4. 中断用一个 `name === 'AbortError'` 的错误抛出，与 fetch 的行为一致。
 */

import {
  SSE_DONE_SENTINEL,
  formatSseFrame,
  sseResponseHeaders,
  validateSsePayload,
  type SseEvent,
  type SseEventName,
} from '@spirit/contracts';

/**
 * 分片大小。
 *
 * 7 与 UTF-8 里 CJK 的 3 字节宽度互质——只要分片大小是 3 的倍数，
 * 汉字就永远会被整块切出来，那个"跨片多字节字符"的 bug 就复现不了。
 */
export const MOCK_CHUNK_SIZE = 7;

/** 分片间隔（**按帧计时**，见下）。太慢会让人以为卡住，太快就看不出流式效果。 */
export const MOCK_CHUNK_DELAY_MS = 18;

/** 中断时抛出的错误文案（与浏览器 abort 的表现保持一致）。 */
export const MOCK_ABORT_MESSAGE = '已停止生成';

export interface MockSseFrame {
  event: SseEventName;
  payload: unknown;
}

/** 按固定体积切片；**按字节数组切**，而不是按字符串切。 */
function sliceBytes(bytes: Uint8Array, size: number): Uint8Array[] {
  const chunks: Uint8Array[] = [];
  for (let offset = 0; offset < bytes.length; offset += size) {
    chunks.push(bytes.subarray(offset, offset + size));
  }
  return chunks;
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      clearTimeout(timer);
      reject(abortError());
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** 造一个与 fetch abort 表现一致的错误（`chat-stream` 只认 `name`）。 */
export function abortError(): Error {
  const error = new Error(MOCK_ABORT_MESSAGE);
  error.name = 'AbortError';
  return error;
}

/**
 * 把事件序列变成 `Response`。
 *
 * 每个事件在发送前都过一次 `validateSsePayload`：Mock 自己发出不合契约的帧，
 * 比不实现 Mock 更糟——那会让前端在 Mock 下调通、接上真后端就崩。
 * 校验失败时宁可把错误**发出去**（前端会走 onParseError 分支），也不静默吞掉。
 */
export function mockSseResponse(
  frames: readonly MockSseFrame[],
  signal: AbortSignal,
  finalError?: unknown,
): Response {
  const encoder = new TextEncoder();

  const stream = new ReadableStream<Uint8Array>({
    async start(controller) {
      try {
        for (const frame of frames) {
          const valid = validateSsePayload(frame.event, frame.payload);
          if (valid === null) {
            throw new Error(`mock-sse: ${frame.event} 帧不符合契约，拒绝发送`);
          }
          const text = formatSseFrame(frame.event, valid);
          // 分片是给解码器的压力测试，**不是**节奏控制：一帧的全部字节一次性入队，
          // 节奏由下面按帧计时的 delay 决定。曾经写成"每片都等 18ms"，而
          // `deltaFrames` 是**一个字符一帧**，于是一条 200 字的回答要流 25 秒——
          // 看起来就像卡死了（冒烟脚本也因此超时）。
          for (const chunk of sliceBytes(encoder.encode(text), MOCK_CHUNK_SIZE)) {
            controller.enqueue(chunk);
          }
          await delay(MOCK_CHUNK_DELAY_MS, signal);
        }
        if (finalError !== undefined) {
          throw finalError;
        }
        controller.close();
      } catch (error) {
        // `error()` 会让已入队的字节仍然可读，随后流以错误结束——
        // 这正是真实中断的语义：用户已经看到的内容不该被抹掉。
        controller.error(error);
      }
    },
  });

  return new Response(stream, {
    status: 200,
    headers: sseResponseHeaders(),
  });
}

/**
 * 把一段完整回答拆成 delta 事件。
 *
 * **按码点切**（`Array.from`）而不是 `slice`：`slice` 会从中间劈开一个代理对，
 * 让一个 emoji 变成两个乱码字符。
 */
export function deltaFrames(text: string, startIndex = 0): MockSseFrame[] {
  const characters = Array.from(text);
  return characters.map((content, index) => ({
    event: 'delta' as const,
    payload: { index: startIndex + index, content },
  }));
}

/** 构造一个 `error` 事件（安全拦截/额度/限流都用它）。 */
export function errorFrame(payload: {
  code: number;
  message: string;
  safety?: unknown;
}): MockSseFrame {
  return { event: 'error', payload };
}

/**
 * 上游故障的统一上报方式。
 *
 * **抛错而不是发 error 帧**：真实上游断连时，服务端往往来不及写 error 帧
 * （连接已经坏了），前端看到的是"流被截断"。把这条路径也 Mock 出来，
 * 才能验证前端在"没有 error 帧的失败"下也会结束加载态。
 */
export function upstreamFailure(): Error {
  const error = new Error('上游连接被重置');
  error.name = 'TypeError';
  return error;
}

/** 供测试断言用：解析一段 Mock 帧序列。 */
export function collectFrames(frames: readonly MockSseFrame[]): SseEvent[] {
  const events: SseEvent[] = [];
  for (const frame of frames) {
    const valid = validateSsePayload(frame.event, frame.payload);
    if (valid === null) {
      continue;
    }
    events.push({ event: frame.event, data: valid, raw: formatSseFrame(frame.event, valid) });
  }
  return events;
}

/** 哨兵常量再导出：测试里要断言"Mock 不发送它"。 */
export const MOCK_NEVER_SENDS = SSE_DONE_SENTINEL;
